/**
 * Giulio Mini Chat — bubble pop-up above the floating mascot.
 * Context-aware quick action chips + custom input + SSE streaming AI replies.
 * Uses the same /api/ai/chat/stream endpoint as the full /assistente page.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { X, Send, Sparkles, ExternalLink, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Mascot from '@/components/Mascot';
import { toast } from 'sonner';
import MarkdownMessage from '@/components/MarkdownMessage';
import { track } from '@/lib/analytics';
import { useGiulioContext, pathToLabel } from '@/context/GiulioContext';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_ACTIONS_BY_PATH = {
  '/':             ['Dammi 3 terreni consigliati', 'Spiegami cosa posso fare qui', 'Come inizio con AgriNexus?'],
  '/esplora':      ['Vigneti in Toscana sotto 300k€', 'Spiegami i filtri', 'Terreni per olivicoltura in Puglia'],
  '/mappa':        ['Zoom su Puglia', 'Che significano i colori dei pin?', 'Terreni vicino alla mia posizione'],
  '/partite':      ['Olive Coratina Puglia disponibili?', 'Come vendo la mia partita?', 'Cosa vuol dire qualità DOP?'],
  '/pubblica':     ['Aiutami con la descrizione', 'Che prezzo mettere?', 'Come scelgo le foto giuste?'],
  '/partite/pubblica': ['Aiutami col titolo partita', 'Pacchetto standard o DOP?', 'Come calcolare €/quintale'],
  '/stima-quintali': ['Quanti q da 5 ha di olivi?', 'Come scattare foto efficaci?', 'Confidenza bassa, cosa fare?'],
  '/suggeriti':    ['Perché proprio questi terreni?', 'Come cambiare le preferenze?', 'Dammi altre opzioni'],
  '/preferenze':   ['Che preferenze impostare?', 'Spiegami il matching', 'Raggio di ricerca ottimale?'],
  '/dashboard':    ['Riepilogo dei miei annunci', 'Come ottimizzare le visite?', 'Suggerimenti per oggi'],
  '/chi-siamo':    ['Chi siete?', 'Com\'è nata AgriNexus?', 'Quali sono i vostri valori?'],
};
const DEFAULT_ACTIONS = ['Che cos\'è AgriNexus?', 'Come inizio?', 'Chi può usarla?'];

function matchQuickActions(path) {
  if (QUICK_ACTIONS_BY_PATH[path]) return QUICK_ACTIONS_BY_PATH[path];
  const keys = Object.keys(QUICK_ACTIONS_BY_PATH).sort((a, b) => b.length - a.length);
  const key = keys.find(k => path.startsWith(k) && k !== '/');
  return (key && QUICK_ACTIONS_BY_PATH[key]) || DEFAULT_ACTIONS;
}

/** Quick actions quando c'è un oggetto specifico in contesto (listing/batch). */
function contextualQuickActions(ctx) {
  if (!ctx) return null;
  if (ctx.kind === 'listing') {
    return [
      'Parlami di questo terreno',
      'È conveniente per il prezzo?',
      'Cosa posso coltivarci?',
    ];
  }
  if (ctx.kind === 'batch') {
    return [
      'Parlami di questa partita',
      'Il prezzo al quintale è di mercato?',
      'A chi potrei venderla/comprarla?',
    ];
  }
  return null;
}

export default function GiulioMiniChat({ open, onClose }) {
  const loc = useLocation();
  const { user } = useAuth();
  const { context, clearContext } = useGiulioContext();
  const [messages, setMessages] = useState([]); // {role, content, streaming?}
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const inputRef = useRef(null);
  const ctxActions = contextualQuickActions(context);
  const actions = ctxActions || matchQuickActions(loc.pathname);
  const pageLabel = pathToLabel(loc.pathname);

  // Scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (open && inputRef.current && !messages.length) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]); // eslint-disable-line

  // Abort stream on close
  useEffect(() => {
    if (!open && abortRef.current) {
      try { abortRef.current(); } catch {}
      abortRef.current = null;
    }
  }, [open]);

  if (!open) return null;

  const sendMessage = async (text) => {
    const content = (text || '').trim();
    if (!content || busy) return;
    if (!user) {
      // redirect: salvo la domanda per il full assistant
      try { sessionStorage.setItem('giulio.pending', content); } catch {}
      window.location.href = '/login';
      return;
    }
    track('giulio_mini_chat_send', { page: loc.pathname });
    setInput('');
    setBusy(true);
    setMessages(m => [...m, { role: 'user', content }, { role: 'assistant', content: '', streaming: true }]);

    try {
      const token = localStorage.getItem('agn_token');
      // Costruzione context payload leggero per il backend
      let ctxPayload = null;
      if (context) {
        if (context.kind === 'listing' && context.data) {
          ctxPayload = { kind: 'listing', listing_id: context.data.listing_id };
        } else if (context.kind === 'batch' && context.data) {
          ctxPayload = { kind: 'batch', batch_id: context.data.batch_id };
        }
      }
      const resp = await fetch(`${API_BASE}/ai/chat/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: content,
          session_id: sessionId,
          page_context: ctxPayload,
          page_path: loc.pathname,
        }),
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let acc = '';
      let sid = sessionId;
      abortRef.current = () => { try { reader.cancel(); } catch {} };
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.split('\n').find(l => l.startsWith('data: '));
          if (!line) continue;
          try {
            const ev = JSON.parse(line.slice(6));
            if (ev.type === 'start') { sid = ev.session_id; }
            else if (ev.type === 'tool') {
              if (!acc) setMessages(m => m.map((mm, i) => (i === m.length - 1 && mm.streaming) ? { ...mm, content: ev.label + '…' } : mm));
            }
            else if (ev.type === 'delta') {
              acc += ev.text;
              setMessages(m => m.map((mm, i) => (i === m.length - 1 && mm.streaming) ? { ...mm, content: acc } : mm));
            } else if (ev.type === 'done') {
              setMessages(m => m.map((mm, i) => (i === m.length - 1 && mm.streaming) ? { ...mm, content: acc, streaming: false } : mm));
              if (sid && sid !== sessionId) setSessionId(sid);
            } else if (ev.type === 'error') {
              throw new Error(ev.error || ev.message || 'AI error');
            }
          } catch (e) { if (e instanceof SyntaxError) continue; throw e; }
        }
      }
    } catch (e) {
      toast.error(e.message || 'Errore AI');
      setMessages(m => {
        const copy = [...m];
        if (copy.length && copy[copy.length - 1].streaming) {
          copy[copy.length - 1] = { role: 'assistant', content: 'Scusa, qualcosa è andato storto. Riprova.' };
        }
        return copy;
      });
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  };

  return (
    <div
      data-testid="giulio-mini-chat"
      className="fixed z-50 bottom-24 right-3 sm:right-6 w-[calc(100vw-24px)] sm:w-[380px] max-w-[380px] h-[520px] max-h-[70vh] bg-background border-2 border-accent/50 rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300"
    >
      {/* Header */}
      <div className="px-4 py-3 bg-foreground text-background flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-full overflow-hidden shrink-0 bg-background border border-accent/40">
            <Mascot pose="wave" size={44} shadow={false} animated={false} className="pointer-events-none -mt-1"/>
          </div>
          <div className="min-w-0">
            <div className="font-medium text-sm leading-tight">Giulio</div>
            <div className="text-[10px] text-background/60 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-accent inline-block"/> Assistente AI online
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Link to="/assistente" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-full hover:bg-background/10" aria-label="Apri chat completa" data-testid="giulio-mini-chat-fullscreen">
            <ExternalLink size={14}/>
          </Link>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-full hover:bg-background/10" aria-label="Chiudi" data-testid="giulio-mini-chat-close">
            <X size={16}/>
          </button>
        </div>
      </div>

      {/* Scroll area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-secondary/30">
        {/* Chip di contesto SEMPRE visibile */}
        <div className="flex items-center gap-1.5 flex-wrap mb-1">
          {context && context.label ? (
            <div
              className="inline-flex items-center gap-1.5 bg-terracotta/10 border border-terracotta/30 text-terracotta rounded-full pl-3 pr-1 py-1 text-[11px] font-medium"
              data-testid="giulio-context-chip"
            >
              <Sparkles size={10}/>
              <span>Stai guardando:</span>
              <b className="truncate max-w-[150px]">{context.label}</b>
              <button
                onClick={clearContext}
                className="w-5 h-5 rounded-full hover:bg-terracotta/20 grid place-items-center ml-0.5"
                aria-label="Rimuovi contesto"
                data-testid="giulio-context-clear"
              >
                <X size={10}/>
              </button>
            </div>
          ) : (
            <div
              className="inline-flex items-center gap-1.5 bg-secondary border border-border text-muted-foreground rounded-full px-3 py-1 text-[11px] font-medium"
              data-testid="giulio-context-chip"
            >
              <Sparkles size={10}/>
              <span>Pagina:</span>
              <b className="truncate max-w-[160px] text-foreground">{pageLabel}</b>
            </div>
          )}
        </div>
        {messages.length === 0 && (
          <div>
            {/* Giulio welcome bubble */}
            <div className="mb-3" data-testid="giulio-welcome-bubble">
              <div className="rounded-2xl rounded-bl-sm bg-background border border-border px-3 py-2.5 text-sm leading-relaxed">
                <div className="font-medium text-xs">Ciao! Sono <b className="text-chartreuse">Giulio</b>, l'assistente AI di <b>AgriNexus</b>.</div>
                <div className="mt-1.5 text-[12px] text-muted-foreground">
                  Chiedimi di <b className="text-foreground">colture, PAC, normative</b>, stima quintali, o come usare il sito. Posso anche leggere foto e visure.
                </div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground mb-2">
              {ctxActions ? 'Posso aiutarti con questo:' : 'Ecco cosa posso fare per te qui:'}
            </div>
            <div className="flex flex-col gap-2">
              {actions.map((a, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(a)}
                  disabled={busy}
                  data-testid={`giulio-quick-action-${i}`}
                  className="text-left text-sm bg-background border border-border rounded-xl px-3 py-2.5 hover:border-terracotta hover:bg-terracotta/5 transition disabled:opacity-50"
                >
                  <span className="text-terracotta mr-1.5">✨</span>{a}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-muted-foreground mt-4 text-center">
              Oppure scrivi la tua domanda qui sotto 👇
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${m.role === 'user' ? 'whitespace-pre-wrap' : ''} ${
              m.role === 'user'
                ? 'bg-foreground text-background rounded-br-sm'
                : 'bg-background border border-border rounded-bl-sm'
            }`}>
              {m.role === 'assistant' && m.content ? <MarkdownMessage content={m.content}/> : (m.content || (m.streaming && <Loader2 size={14} className="animate-spin inline"/>))}
              {m.streaming && m.content && <span className="inline-block w-1 h-3 bg-terracotta ml-0.5 animate-pulse"/>}
            </div>
          </div>
        ))}
      </div>

      {/* Input */}
      <form
        onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
        className="shrink-0 border-t border-border bg-background p-3 flex items-end gap-2"
      >
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
          placeholder={user ? 'Scrivi a Giulio…' : 'Accedi per chattare…'}
          rows={1}
          disabled={busy}
          data-testid="giulio-mini-chat-input"
          className="flex-1 resize-none text-sm bg-secondary/40 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-terracotta/40 max-h-24"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          data-testid="giulio-mini-chat-send"
          className="w-10 h-10 rounded-full bg-foreground text-background grid place-items-center disabled:opacity-40 hover:bg-foreground/90 shrink-0"
          aria-label="Invia"
        >
          {busy ? <Loader2 size={16} className="animate-spin"/> : <Send size={14}/>}
        </button>
      </form>
    </div>
  );
}
